import React, { useState, useEffect, useMemo } from "react";
import { PRAYOGAM_DIVISIONS } from "../data/prayogams/prayogamIndex";
import { SAMAM_POORVAM_DATA } from "../data/prayogams/samamPoorvamData";
import { SAMAM_APARAM_DATA } from "../data/prayogams/samamAparamData";
import { yajurPoorvamData } from "../data/prayogams/yajurPoorvamData";
import {
  BookOpen,
  Flame,
  BookMarked,
  ShieldAlert,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Search,
  Copy,
  Check,
  Type,
  Star,
  Menu,
  X
} from "lucide-react";

export default function PrayogamsPage() {
  const [activeDivision, setActiveDivision] = useState("samam-poorvam");
  const [activeTopicId, setActiveTopicId] = useState("mangala-dravya");
  const [expandedDivisions, setExpandedDivisions] = useState({
    "samam-poorvam": true,
    "samam-aparam": false,
    "yajur-poorvam": true,
    "yajur-aparam": false
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("split"); // 'split' | 'deva' | 'tamil'
  const [fontSize, setFontSize] = useState(18); // default font size px
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [onlyBookmarks, setOnlyBookmarks] = useState(false);

  // User Bookmarks state persisted in localStorage
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const saved = localStorage.getItem("ahobilam_prayogam_bookmarks");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem("ahobilam_prayogam_bookmarks", JSON.stringify(bookmarks));
    } catch (err) {
      console.error("Failed to save bookmarks to localStorage:", err);
    }
  }, [bookmarks]);

  // Combine datasets
  const allTopicData = useMemo(() => ({
    ...SAMAM_POORVAM_DATA,
    ...SAMAM_APARAM_DATA,
    ...yajurPoorvamData
  }), []);

  // Continuous ordered list of all topics for Next/Previous topic navigation
  const flatTopicList = useMemo(() => {
    const list = [];
    PRAYOGAM_DIVISIONS.forEach((div) => {
      div.topics.forEach((t) => {
        list.push({ ...t, divId: div.id, divTitle: div.title });
      });
    });
    return list;
  }, []);

  const currentTopicIndex = flatTopicList.findIndex((t) => t.id === activeTopicId);
  const prevTopic = currentTopicIndex > 0 ? flatTopicList[currentTopicIndex - 1] : null;
  const nextTopic = currentTopicIndex < flatTopicList.length - 1 ? flatTopicList[currentTopicIndex + 1] : null;

  const navigateToTopic = (targetTopic) => {
    if (!targetTopic) return;
    setActiveDivision(targetTopic.divId);
    setActiveTopicId(targetTopic.id);
    setExpandedDivisions((prev) => ({ ...prev, [targetTopic.divId]: true }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Toggle bookmark for a topic
  const toggleBookmark = (topicId) => {
    setBookmarks((prev) =>
      prev.includes(topicId) ? prev.filter((id) => id !== topicId) : [...prev, topicId]
    );
  };

  // Toggle division collapse
  const toggleDivision = (divId) => {
    setExpandedDivisions((prev) => ({
      ...prev,
      [divId]: !prev[divId]
    }));
  };

  // Get current active division object
  const currentDivisionObj = useMemo(() => {
    return PRAYOGAM_DIVISIONS.find((d) => d.id === activeDivision) || PRAYOGAM_DIVISIONS[0];
  }, [activeDivision]);

  // Get current active topic details
  const activeTopic = useMemo(() => {
    return allTopicData[activeTopicId] || {
      titleDeva: "प्रयोग शीर्षकम्",
      titleTamil: "பிரயோகத் தலைப்பு",
      content: []
    };
  }, [allTopicData, activeTopicId]);

  // Copy text to clipboard
  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Filter topics by search query or bookmarks
  const filteredDivisions = useMemo(() => {
    return PRAYOGAM_DIVISIONS.map((div) => ({
      ...div,
      topics: div.topics.filter((t) => {
        const matchesSearch =
          !searchQuery.trim() ||
          t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          t.titleTamil.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesBookmark = !onlyBookmarks || bookmarks.includes(t.id);
        return matchesSearch && matchesBookmark;
      })
    })).filter((div) => div.topics.length > 0);
  }, [searchQuery, onlyBookmarks, bookmarks]);

  const getIcon = (iconName) => {
    switch (iconName) {
      case "BookOpen": return <BookOpen className="w-5 h-5 text-amber-600" />;
      case "Flame": return <Flame className="w-5 h-5 text-orange-600" />;
      case "BookMarked": return <BookMarked className="w-5 h-5 text-blue-600" />;
      case "ShieldAlert": return <ShieldAlert className="w-5 h-5 text-red-600" />;
      default: return <BookOpen className="w-5 h-5 text-amber-600" />;
    }
  };

  return (
    <div className="min-h-screen bg-amber-50/30 text-gray-900 pb-12">
      {/* Header Banner */}
      <header className="bg-gradient-to-r from-amber-900 via-amber-800 to-yellow-900 text-white py-6 px-4 shadow-md sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center justify-between w-full md:w-auto">
            <div>
              <span className="text-amber-300 font-semibold tracking-wider text-xs uppercase">वैदिक प्रयोग संग्रहः</span>
              <h1 className="text-2xl md:text-3xl font-bold font-serif text-amber-100 mt-0.5">
                பிரயோகங்கள் (Prayogams)
              </h1>
            </div>

            {/* Mobile Navigation Drawer Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg bg-amber-800/80 text-amber-100 hover:bg-amber-700"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Quick Search & Filter Controls */}
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Search Input */}
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-amber-400" />
              <input
                type="text"
                placeholder="தேடுக (Search)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-1.5 bg-amber-950/60 border border-amber-700/50 rounded-lg text-amber-100 placeholder-amber-400/70 focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
              />
            </div>

            {/* Bookmarks Quick Filter Button */}
            <button
              onClick={() => setOnlyBookmarks(!onlyBookmarks)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors border ${
                onlyBookmarks
                  ? "bg-amber-400 text-amber-950 border-amber-300"
                  : "bg-amber-950/60 text-amber-200 border-amber-700/50 hover:bg-amber-800"
              }`}
            >
              <Star className={`w-3.5 h-3.5 ${onlyBookmarks ? "fill-amber-950" : "text-amber-300"}`} />
              <span>புக்மார்க்குகள் ({bookmarks.length})</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Sidebar: Multi-Level Collapsible Navigation Menu (Desktop & Mobile Drawer) */}
        <aside
          className={`lg:col-span-4 bg-white rounded-xl shadow-sm border border-amber-200/70 p-4 h-fit transition-all duration-300 ${
            isMobileMenuOpen ? "block fixed inset-x-4 top-20 z-40 max-h-[80vh] overflow-y-auto shadow-2xl border-amber-300" : "hidden lg:block"
          }`}
        >
          <div className="flex items-center justify-between mb-3 pb-2 border-b border-amber-200">
            <h2 className="text-md font-bold text-amber-900 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-amber-700" />
              பிரயோகப் பொருளடக்கம் (Menu Index)
            </h2>
            {isMobileMenuOpen && (
              <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-500 hover:text-gray-800">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          <div className="space-y-3">
            {filteredDivisions.map((div) => {
              const isExpanded = expandedDivisions[div.id] || Boolean(searchQuery) || onlyBookmarks;
              return (
                <div key={div.id} className="border border-amber-100 rounded-lg overflow-hidden">
                  {/* Division Header */}
                  <button
                    onClick={() => toggleDivision(div.id)}
                    className={`w-full flex items-center justify-between p-3 text-left transition-colors ${
                      activeDivision === div.id
                        ? "bg-amber-100/70 text-amber-950 font-bold"
                        : "bg-amber-50/40 hover:bg-amber-100/50 text-gray-800"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      {getIcon(div.icon)}
                      <div>
                        <div className="text-sm font-semibold">{div.title}</div>
                        <div className="text-xs text-gray-500 font-normal">{div.subtitle}</div>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-amber-700" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {/* Topic Items Submenu */}
                  {isExpanded && (
                    <div className="bg-white py-1 divide-y divide-gray-50">
                      {div.topics.length === 0 ? (
                        <div className="p-3 text-xs text-gray-400 italic">தலைப்புகள் ஏதும் இல்லை</div>
                      ) : (
                        div.topics.map((t) => {
                          const isActive = activeTopicId === t.id;
                          const isBookmarked = bookmarks.includes(t.id);
                          return (
                            <div
                              key={t.id}
                              className={`flex items-center justify-between px-3 py-2 text-xs transition-colors ${
                                isActive ? "bg-amber-600 text-white font-medium" : "hover:bg-amber-50 text-gray-700"
                              }`}
                            >
                              <button
                                onClick={() => {
                                  setActiveDivision(div.id);
                                  setActiveTopicId(t.id);
                                  setIsMobileMenuOpen(false);
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="flex-1 text-left flex flex-col gap-0.5 pr-2"
                              >
                                <span className="font-serif text-sm">{t.title}</span>
                                <span className={isActive ? "text-amber-100" : "text-gray-500"}>
                                  {t.titleTamil}
                                </span>
                              </button>

                              {/* Bookmark Toggle Icon */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleBookmark(t.id);
                                }}
                                className="p-1 rounded hover:bg-amber-700/20"
                                title={isBookmarked ? "Remove Bookmark" : "Add Bookmark"}
                              >
                                <Star
                                  className={`w-4 h-4 ${
                                    isBookmarked
                                      ? "fill-amber-400 text-amber-400"
                                      : isActive
                                      ? "text-amber-200"
                                      : "text-gray-300 hover:text-amber-500"
                                  }`}
                                />
                              </button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Right Main Viewer: Devanagari Sanskrit + Tamil Translation */}
        <main className="lg:col-span-8 bg-white rounded-xl shadow-sm border border-amber-200/70 p-4 md:p-6">
          {/* Controls Header */}
          <div className="flex flex-wrap items-center justify-between gap-4 pb-4 border-b border-amber-200 mb-6">
            <div>
              {/* Tiny Location Identifier Header */}
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 bg-amber-100/90 px-2.5 py-0.5 rounded-full border border-amber-300 mb-1.5">
                <span>-- {currentDivisionObj.title} --</span>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                  Topic {currentTopicIndex + 1} of {flatTopicList.length} • Page {activeTopic.pageNumber || 1}
                </span>

                {/* Bookmark Toggle Button for Topic */}
                <button
                  onClick={() => toggleBookmark(activeTopicId)}
                  className="flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border border-amber-300 text-amber-800 bg-amber-50 hover:bg-amber-100"
                >
                  <Star
                    className={`w-3.5 h-3.5 ${
                      bookmarks.includes(activeTopicId) ? "fill-amber-500 text-amber-500" : "text-gray-400"
                    }`}
                  />
                  <span>{bookmarks.includes(activeTopicId) ? "புக்மார்க் செய்யப்பட்டது" : "புக்மார்க் செய்க"}</span>
                </button>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold font-serif text-amber-950 mt-1">
                {activeTopic.titleDeva}
              </h2>
              <h3 className="text-md text-amber-800 font-medium">{activeTopic.titleTamil}</h3>
            </div>

            {/* Toolbar Controls */}
            <div className="flex flex-wrap items-center gap-3 bg-amber-50/80 p-2 rounded-lg border border-amber-200 w-full md:w-auto justify-between">
              {/* View Toggle */}
              <div className="flex bg-white rounded border border-amber-200 p-0.5 text-xs">
                <button
                  onClick={() => setViewMode("split")}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    viewMode === "split" ? "bg-amber-700 text-white font-medium" : "text-gray-700 hover:bg-amber-100"
                  }`}
                >
                  இரு மொழிகள் (Both)
                </button>
                <button
                  onClick={() => setViewMode("deva")}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    viewMode === "deva" ? "bg-amber-700 text-white font-medium" : "text-gray-700 hover:bg-amber-100"
                  }`}
                >
                  देवनागरी
                </button>
                <button
                  onClick={() => setViewMode("tamil")}
                  className={`px-2.5 py-1 rounded transition-colors ${
                    viewMode === "tamil" ? "bg-amber-700 text-white font-medium" : "text-gray-700 hover:bg-amber-100"
                  }`}
                >
                  தமிழ் உரை
                </button>
              </div>

              {/* Font Size Adjuster */}
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <Type className="w-3.5 h-3.5 text-amber-700" />
                <button
                  onClick={() => setFontSize((s) => Math.max(14, s - 2))}
                  className="w-7 h-7 rounded bg-white hover:bg-amber-100 border border-amber-200 flex items-center justify-center font-bold text-gray-700 shadow-sm"
                >
                  -
                </button>
                <span className="font-semibold">{fontSize}px</span>
                <button
                  onClick={() => setFontSize((s) => Math.min(28, s + 2))}
                  className="w-7 h-7 rounded bg-white hover:bg-amber-100 border border-amber-200 flex items-center justify-center font-bold text-gray-700 shadow-sm"
                >
                  +
                </button>
              </div>
            </div>
          </div>

          {/* Content Body */}
          {!activeTopic.content || activeTopic.content.length === 0 ? (
            <div className="py-16 text-center text-gray-400 font-serif">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-amber-300" />
              <p>இப்பிரிவின் உரைகள் விரைவில் சேர்க்கப்படும்...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {activeTopic.content
                .map((item, idx) => {
                  const devaText = item.deva || item.devanagari || "";
                  const tamilText = item.tamilMeaning || item.tamil || "";
                  return { item, idx, devaText, tamilText };
                })
                .filter(({ devaText, tamilText }) => devaText.trim().length > 0 || tamilText.trim().length > 0)
                .map(({ item, idx, devaText, tamilText }) => (
                <div
                  key={idx}
                  className="p-4 md:p-5 rounded-xl bg-amber-50/40 border border-amber-200/60 hover:border-amber-300 transition-colors relative group shadow-sm"
                >
                  {/* Copy Button */}
                  <button
                    onClick={() => handleCopy(`${devaText}\n${tamilText}`, idx)}
                    className="absolute right-3 top-3 p-1.5 rounded-md bg-white border border-amber-200 text-gray-500 hover:text-amber-800 hover:bg-amber-100 opacity-90 md:opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Copy Text"
                  >
                    {copiedIndex === idx ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </button>

                  {/* Devanagari Sanskrit */}
                  {(viewMode === "split" || viewMode === "deva") && devaText.trim() && (
                    <div
                      className="font-serif text-amber-950 font-semibold leading-relaxed mb-3 pr-8 whitespace-pre-line"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {devaText}
                    </div>
                  )}

                  {/* Tamil Transliteration & Meaning */}
                  {(viewMode === "split" || viewMode === "tamil") && tamilText.trim() && (
                    <div className="space-y-2 border-t border-amber-200/60 pt-3">
                      {item.tamilTranslit && (
                        <div className="text-amber-800 text-sm italic font-serif whitespace-pre-line">
                          {item.tamilTranslit}
                        </div>
                      )}
                      <div
                        className="text-gray-800 font-sans leading-relaxed whitespace-pre-line"
                        style={{ fontSize: `${Math.max(14, fontSize - 2)}px` }}
                      >
                        <span className="text-xs uppercase font-bold text-amber-700 tracking-wider mr-2">
                          [தமிழ் உரை]:
                        </span>
                        {tamilText}
                      </div>
                    </div>
                  )}

                  {/* Tiny Location Footer on each card */}
                  <div className="mt-3 pt-2 border-t border-amber-200/50 flex items-center justify-between text-[11px] text-amber-800/80 font-medium">
                    <span>-- {currentDivisionObj.title} --</span>
                    <span className="text-[10px] text-amber-700/70">பாகம் {idx + 1}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Continuous Next / Previous Topic Navigation Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mt-8 pt-6 border-t border-amber-200">
            {prevTopic ? (
              <button
                onClick={() => navigateToTopic(prevTopic)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-100/80 hover:bg-amber-200 text-amber-950 text-xs md:text-sm font-semibold transition-colors border border-amber-300"
              >
                <ChevronLeft className="w-4 h-4" />
                <div className="text-left">
                  <div className="text-[10px] text-amber-700 font-normal uppercase">முந்தைய பிரயோகம்</div>
                  <div className="font-serif">{prevTopic.titleTamil}</div>
                </div>
              </button>
            ) : <div />}

            {nextTopic ? (
              <button
                onClick={() => navigateToTopic(nextTopic)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-amber-700 hover:bg-amber-800 text-white text-xs md:text-sm font-semibold transition-colors shadow-sm ml-auto"
              >
                <div className="text-right">
                  <div className="text-[10px] text-amber-200 font-normal uppercase">அடுத்த பிரயோகம்</div>
                  <div className="font-serif">{nextTopic.titleTamil}</div>
                </div>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : <div />}
          </div>

          {/* Page Bottom Tiny Footer Location Identifier */}
          <div className="mt-6 pt-4 border-t border-amber-100 text-center text-[11px] font-semibold text-amber-800/70">
            -- {currentDivisionObj.title} | {activeTopic.titleDeva || activeTopic.titleTamil} --
          </div>
        </main>
      </div>
    </div>
  );
}
